// src/utils/apiErrors.js

/**
 * Custom error classes for API errors
 * Provides consistent error structure across the application
 */

/**
 * Base API Error class
 */
export class APIError extends Error {
  constructor(message, statusCode, errors = []) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.errors = errors;
    this.isOperational = true;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  getUserMessage() {
    if (this.errors && this.errors.length > 0) {
      return this.errors[0].message || this.message;
    }
    return this.message;
  }
}

/**
 * Validation Error (400)
 */
export class ValidationError extends APIError {
  constructor(message, errors = []) {
    super(message, 400, errors);
    this.name = "ValidationError";
  }

  getErrorsByField() {
    const errorsByField = {};
    this.errors.forEach((error) => {
      if (error.field) {
        if (!errorsByField[error.field]) {
          errorsByField[error.field] = [];
        }
        errorsByField[error.field].push(error.message || error);
      }
    });
    return errorsByField;
  }
}

/**
 * Authentication Error (401)
 */
export class AuthenticationError extends APIError {
  constructor(message = "Authentication failed. Please log in") {
    super(message, 401);
    this.name = "AuthenticationError";
  }
}

/**
 * Authorization Error (403)
 */
export class AuthorizationError extends APIError {
  constructor(message = "You do not have permission to perform this action") {
    super(message, 403);
    this.name = "AuthorizationError";
  }
}

/**
 * Not Found Error (404)
 */
export class NotFoundError extends APIError {
  constructor(message = "The requested resource was not found") {
    super(message, 404);
    this.name = "NotFoundError";
  }
}

/**
 * Network Error (0)
 */
export class NetworkError extends APIError {
  constructor(
    message = "Unable to connect to server. Please check your internet connection"
  ) {
    super(message, 0);
    this.name = "NetworkError";
  }
}

/**
 * Server Error (500)
 */
export class ServerError extends APIError {
  constructor(
    message = "An internal server error occurred. Please try again later"
  ) {
    super(message, 500);
    this.name = "ServerError";
  }
}

/**
 * Transform technical backend error messages into user-friendly messages
 * @param {string} message - Original error message from backend
 * @returns {string} User-friendly error message
 */
function getUserFriendlyMessage(message) {
  if (!message) return "An error occurred";

  // Duplicate email error
  if (message.includes("Duplicate field value") && message.includes("@")) {
    return "User already exists";
  }

  return message;
}

/**
 * Normalize axios errors to consistent APIError format
 * @param {Error} error - Axios error object
 * @returns {APIError} Normalize error object
 */
export function normalizeError(error) {
  // If already an APIError, return as-is
  if (error instanceof APIError) {
    return error;
  }

  // Backend returned error response
  if (error.response) {
    const { status, data } = error.response;
    const rawMessage = data?.message || "An error occurred";
    const message = getUserFriendlyMessage(rawMessage);
    const errors = data?.errors || [];

    switch (status) {
      case 400:
        return new ValidationError(message, errors);
      case 401:
        return new AuthenticationError(message);
      case 403:
        return new AuthorizationError(message);
      case 404:
        return new NotFoundError(message);
      case 500:
      case 502:
      case 503:
        return new ServerError(message);
      default:
        return new APIError(message, status, errors);
    }
  }

  // Network error
  if (error.request) {
    return new NetworkError();
  }

  // Other error
  return new APIError(error.message || "An unexpected error occurred", 500);
}
